import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle2, XCircle, X, AlertCircle, Gamepad2, Tag, Package, Clock } from 'lucide-react';
import { FILL_GRADIENT } from '../../constants/gradients';
import type { StudioNotification } from '../../services/studioNotificationsService';
import { projectsService } from '../../services/projectsService';
import { useProjectsStore } from '../../store/useProjectsStore';

interface ReviewCompleteNotificationModalProps {
  notification: StudioNotification | null;
  onDismiss: (notificationId: string) => void;
}

const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const ReviewCompleteNotificationModal: React.FC<ReviewCompleteNotificationModalProps> = ({
  notification,
  onDismiss
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchProjects } = useProjectsStore();
  const [isPublishing, setIsPublishing] = useState(false);
  const isOpen = notification !== null;
  const isPassed = notification?.payload.status === 'passed' || notification?.payload.status === 'published';
  const isFailed = notification?.payload.status === 'failed';

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && notification) onDismiss(notification.id);
    };
    if (isOpen) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, notification, onDismiss]);

  const handlePublish = async () => {
    if (!notification) return;
    setIsPublishing(true);
    try {
      await projectsService.togglePublishStatus(notification.payload.game_id, false);
      onDismiss(notification.id);
      if (location.pathname === '/projects') {
        fetchProjects();
      } else {
        navigate(`/projects?tab=completed`);
      }
    } catch {
      setIsPublishing(false);
    }
  };

  const handleDismiss = () => {
    if (!notification) return;
    onDismiss(notification.id);
  };

  const handleOpenEditor = () => {
    if (!notification) return;
    onDismiss(notification.id);
    navigate(`/editor/${notification.payload.game_id}`);
  };

  return (
    <AnimatePresence>
      {isOpen && notification && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          onClick={handleDismiss}
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          style={{ willChange: 'opacity' }}
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-dark-surface rounded-2xl shadow-xl max-w-md w-full overflow-hidden"
            style={{ willChange: 'opacity, transform' }}
          >
            {/* Gradient accent */}
            <div
              className="h-1 w-full"
              style={{ background: isPassed ? FILL_GRADIENT : 'linear-gradient(90deg, #ef4444 0%, #f97316 100%)' }}
            />

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-border">
              <div className="flex items-center gap-3">
                {isPassed ? (
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                ) : (
                  <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                  </div>
                )}
                <h3 className="text-xl font-bold text-gray-900 dark:text-dark-text">
                  {isPassed ? 'Review Passed' : 'Review Failed'}
                </h3>
              </div>
              <button
                onClick={handleDismiss}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">

              {/* Game card — icon + name + meta */}
              <div className="flex items-center gap-4">
                {notification.payload.icon_url ? (
                  <img
                    src={notification.payload.icon_url}
                    alt={notification.payload.game_name}
                    className="w-20 h-20 rounded-2xl object-cover flex-shrink-0 border border-gray-200 dark:border-dark-border"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-2xl flex-shrink-0 bg-gray-100 dark:bg-gray-800 flex items-center justify-center border border-gray-200 dark:border-dark-border">
                    <Gamepad2 className="w-9 h-9 text-gray-400 dark:text-gray-500" />
                  </div>
                )}
                <div className="min-w-0 space-y-1.5">
                  <p className="text-lg font-bold text-gray-900 dark:text-dark-text leading-tight truncate">
                    {notification.payload.game_name}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-dark-text-muted">
                    {notification.payload.bundle_version != null && (
                      <span className="flex items-center gap-1">
                        <Package className="w-3.5 h-3.5" />
                        v{notification.payload.bundle_version}
                      </span>
                    )}
                    {notification.payload.category && (
                      <span className="flex items-center gap-1">
                        <Tag className="w-3.5 h-3.5" />
                        {notification.payload.category}
                      </span>
                    )}
                    {notification.payload.completed_at && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatTimeAgo(notification.payload.completed_at)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              {notification.payload.description && (
                <div className="border-t border-gray-100 dark:border-dark-border pt-4">
                  <p className="text-sm text-gray-500 dark:text-dark-text-muted leading-relaxed line-clamp-3 italic bg-gray-50 dark:bg-gray-800/60 rounded-lg px-3 py-2.5">
                    "{notification.payload.description}"
                  </p>
                </div>
              )}

              {/* Summary message */}
              <div className="border-t border-gray-100 dark:border-dark-border pt-4">
                <p className="text-xs font-medium text-gray-400 dark:text-dark-text-muted uppercase tracking-wide mb-1">Status</p>
                <p className="text-sm text-gray-600 dark:text-dark-text-muted leading-relaxed">
                  {isPassed
                    ? 'Your game passed review and is ready to go live. Publish it to make it available to stream audiences.'
                    : 'Your game did not pass review. Open the editor to fix the issues and resubmit.'
                  }
                </p>
              </div>

              {/* Failure issues */}
              {isFailed && notification.payload.issues && notification.payload.issues.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    Issues to fix ({notification.payload.issues.length})
                  </p>
                  <ul className="space-y-1.5 max-h-36 overflow-y-auto">
                    {notification.payload.issues.map((issue, i) => (
                      <li
                        key={i}
                        className="text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2 leading-snug"
                      >
                        {issue}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-gray-800/50">
              <button
                onClick={isFailed ? handleDismiss : handleDismiss}
                disabled={isPublishing}
                className="px-5 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isFailed ? 'Dismiss' : 'Publish Later'}
              </button>
              <button
                onClick={isPassed ? handlePublish : handleOpenEditor}
                disabled={isPublishing}
                className="px-6 py-2.5 text-sm font-semibold text-white rounded-lg hover:shadow-lg hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: FILL_GRADIENT }}
              >
                {isPublishing ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Publishing...
                  </span>
                ) : isFailed ? 'Fix Issues' : 'Publish'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReviewCompleteNotificationModal;
